import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { type ApiError, type PhoneChangeRequest } from '@vello/api-client';
import {
  PHONE_CHANGE_REASON_CODES, RIDER_REGISTRATION_REASON_CODES, canDecidePhoneChange, canRejectPhoneChange, maySelfApprove, canVerifyPhoneNumber, canWithdrawPhoneChange, emptyPhoneChangeForm, emptyRiderForm,
  normalizePhoneChangeForm, normalizeRiderForm,
  type PhoneChangeForm, type PhoneChangeFormIssue, type PhoneChangeReasonCode, type RiderForm, type RiderFormIssue, type RiderRegistrationReasonCode,
} from '../../src/index';
import { useConsole } from '../console';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePhoneText } from '../components/phone-text';
import { Badge, Button, CheckRow, EmptyResult, Field, FilterBar, Notice, PageHeader, Select, TextArea, TextInput, Cluster, Stack, Text, HairlineRow, Card, CardForm } from '@vello/web-theme';

type Msg = { tone: 'success' | 'danger' | 'warning'; text: string };

/**
 * Passageiros › **Cadastro e telefone** (W9-PROFILES-REGISTRIES-KEYS, D475, ADR-0093; DevMobility E-001/E-005, finding X-09).
 *
 *  - **Cadastrar passageiro**: an operator registers a rider who cannot (or will not) sign up alone — an ordinary RIDER account with
 *    the operator and the reason recorded. The person claims it by signing in with the OTP of that phone.
 *  - **Troca de telefone**: the phone is the login identifier, so changing it is a four-eyes act. One operator proposes the new
 *    number (attesting the person's identity was checked); a DIFFERENT operator approves it here, which moves the phone, ends every
 *    session of the account and audits it. A proposal expires after 24 hours.
 *
 * The server authorizes each call at the account's country and refuses every self-approval; this screen hides what it can foresee.
 */
export function PassengerRegistryScreen(): ReactElement {
  const { runtime, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own requests
  const { confirm } = useConsole();
  const { t } = useT();
  const phoneOf = usePhoneText();
  const { v, fail, explain } = useVocab();
  const api = runtime.accountRegistry;

  const [rider, setRider] = useState<RiderForm>(() => emptyRiderForm(me?.countryCode ?? '', me?.locale ?? '', me?.timezone ?? ''));
  const [riderReason, setRiderReason] = useState<RiderRegistrationReasonCode>('rider_asked_by_phone');
  const [riderIssues, setRiderIssues] = useState<readonly RiderFormIssue[]>([]);
  const [change, setChange] = useState<PhoneChangeForm>(() => emptyPhoneChangeForm());
  const [changeReason, setChangeReason] = useState<PhoneChangeReasonCode>('phone_lost');
  const [changeIssues, setChangeIssues] = useState<readonly PhoneChangeFormIssue[]>([]);
  const [queue, setQueue] = useState<readonly PhoneChangeRequest[] | undefined>(undefined);
  // D502 (Q-015): the code the person read out, per request in the queue.
  const [codes, setCodes] = useState<Readonly<Record<string, string>>>({});
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const refusal = useCallback((e: ApiError): string => (e.code === 'FORBIDDEN' ? t('acr.denied') : explain(e) ?? t('acr.failed', { code: fail(e) })), [t, fail, explain]);

  const load = useCallback(async (): Promise<void> => {
    const res = await api.phoneChangeQueue();
    setQueue(res.ok ? res.value.requests : []);
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);

  const onRegister = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeRiderForm(rider, riderReason);
    if (!normalized.ok) {
      setRiderIssues(normalized.error);
      return;
    }
    setRiderIssues([]);
    setBusy(true);
    const res = await api.registerRider(normalized.value);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMsg({ tone: 'success', text: t('acr.registered', { name: res.value.user.firstName ?? '', id: res.value.user.id }) });
    setRider(emptyRiderForm(rider.countryCode, rider.locale, rider.timezone));
  };

  const onPropose = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizePhoneChangeForm(change, changeReason);
    if (!normalized.ok) {
      setChangeIssues(normalized.error);
      return;
    }
    setChangeIssues([]);
    setBusy(true);
    const res = await api.proposePhoneChange(normalized.value.userId, normalized.value.input);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMsg({ tone: 'warning', text: t(res.value.numberCode !== undefined ? 'acr.change.proposedCode' : 'acr.change.proposed') });
    setChange(emptyPhoneChangeForm());
    await load();
  };

  const onDecide = async (r: PhoneChangeRequest, action: 'approve' | 'reject' | 'withdraw'): Promise<void> => {
    let note: string | undefined;
    if (action !== 'withdraw') {
      const answer = await confirm({
        title: t(action === 'approve' ? 'acr.queue.approveTitle' : 'acr.queue.rejectTitle'), body: t(action === 'approve' ? 'acr.queue.approveBody' : 'acr.queue.rejectBody'),
        confirmLabel: t(action === 'approve' ? 'acr.queue.approve' : 'acr.queue.reject'), cancelLabel: t('acr.cancel'), tone: action === 'approve' ? 'primary' : 'danger', icon: 'shieldAlert',
        notePlaceholder: t('acr.field.note'),
      });
      if (!answer.confirmed) return;
      if (action === 'reject' && (answer.note === undefined || answer.note.trim() === '')) return;
      note = answer.note;
    }
    const res = await api.decidePhoneChange(r.id, action, note);
    setMsg(res.ok ? { tone: 'success', text: action === 'approve' ? t('acr.queue.applied', { n: res.value.sessionsRevoked }) : t('acr.done') } : { tone: 'danger', text: refusal(res.error) });
    if (res.ok) await load();
  };

  const onVerify = async (r: PhoneChangeRequest): Promise<void> => {
    setMsg(undefined);
    const res = await api.verifyPhoneNumber(r.id, (codes[r.id] ?? '').trim());
    if (res.ok) {
      setCodes((c) => ({ ...c, [r.id]: '' }));
      setMsg({ tone: 'success', text: t('acr.number.verified') });
      await load();
      return;
    }
    const reason = res.error.reasonCode;
    setMsg({ tone: 'danger', text: reason === 'PHONE_CHANGE_CODE_INVALID' ? t('acr.number.wrong') : reason === 'PHONE_CHANGE_CODE_EXPIRED' || reason === 'PHONE_CHANGE_CODE_LOCKED' ? t('acr.number.needsResend') : refusal(res.error) });
    await load();
  };

  const onResend = async (r: PhoneChangeRequest): Promise<void> => {
    setMsg(undefined);
    const res = await api.resendPhoneNumberCode(r.id);
    setMsg(res.ok ? { tone: 'warning', text: t('acr.number.resent') } : { tone: 'danger', text: res.error.reasonCode === 'PHONE_CHANGE_CODE_RATE_LIMITED' ? t('acr.number.rateLimited') : refusal(res.error) });
    if (res.ok) await load();
  };

  const rIssue = (i: RiderFormIssue): string | undefined => (riderIssues.includes(i) ? t(`acr.issue.${i}`) : undefined);
  const cIssue = (i: PhoneChangeFormIssue): string | undefined => (changeIssues.includes(i) ? t(`acr.issue.${i}`) : undefined);
  const setR = (k: keyof RiderForm) => (val: string): void => setRider((f) => ({ ...f, [k]: val }));

  return (
    <div>
      <PageHeader title={t('acr.title')} subtitle={t('acr.subtitle')} />
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

      <CardForm onSubmit={(e) => void onRegister(e)} noValidate aria-label={t('acr.register.title')} data-testid="acr-register-form">
        <Stack gap={4}>
          <strong>{t('acr.register.title')}</strong>
          <Text role="caption" tone="secondary" block>{t('acr.register.hint')}</Text>
          <Field label={t('acr.field.firstName')} htmlFor="acr-first" hint={rIssue('firstName')}><TextInput id="acr-first" value={rider.firstName} onChange={(e) => setR('firstName')(e.target.value)} /></Field>
          <Field label={t('acr.field.lastName')} htmlFor="acr-last" hint={rIssue('lastName')}><TextInput id="acr-last" value={rider.lastName} onChange={(e) => setR('lastName')(e.target.value)} /></Field>
          <Field label={t('acr.field.phone')} htmlFor="acr-phone" hint={rIssue('phone')}><TextInput id="acr-phone" type="tel" className="tnum" autoComplete="off" value={rider.phoneE164} onChange={(e) => setR('phoneE164')(e.target.value)} /></Field>
          <Field label={t('acr.field.email')} htmlFor="acr-email" hint={rIssue('email')}><TextInput id="acr-email" type="email" autoComplete="off" value={rider.email} onChange={(e) => setR('email')(e.target.value)} /></Field>
          <Field label={t('acr.field.country')} htmlFor="acr-country" hint={rIssue('country')}><TextInput id="acr-country" value={rider.countryCode} onChange={(e) => setR('countryCode')(e.target.value)} /></Field>
          <Field label={t('acr.field.locale')} htmlFor="acr-locale" hint={rIssue('locale')}><TextInput id="acr-locale" value={rider.locale} onChange={(e) => setR('locale')(e.target.value)} /></Field>
          <Field label={t('acr.field.timezone')} htmlFor="acr-tz" hint={rIssue('timezone')}><TextInput id="acr-tz" value={rider.timezone} onChange={(e) => setR('timezone')(e.target.value)} /></Field>
          <Field label={t('acr.field.reason')} htmlFor="acr-reason">
            <Select id="acr-reason" value={riderReason} onChange={(e) => setRiderReason(e.target.value as RiderRegistrationReasonCode)}>
              {RIDER_REGISTRATION_REASON_CODES.map((c) => <option key={c} value={c}>{v('account.rider_registration_reason', c)}</option>)}
            </Select>
          </Field>
          <Field label={t('acr.field.note')} htmlFor="acr-note"><TextArea id="acr-note" rows={2} maxLength={500} value={rider.note} onChange={(e) => setR('note')(e.target.value)} /></Field>
          <Cluster gap={2} wrap><Button type="submit" variant="primary" disabled={busy} data-testid="acr-register-send">{t('acr.register.send')}</Button></Cluster>
        </Stack>
      </CardForm>

      <CardForm onSubmit={(e) => void onPropose(e)} noValidate aria-label={t('acr.change.title')} data-testid="acr-change-form">
        <Stack gap={4}>
          <strong>{t('acr.change.title')}</strong>
          <Notice tone="warning">{t('acr.change.hint')}</Notice>
          <Field label={t('acr.field.userId')} htmlFor="acr-user" hint={cIssue('userId') ?? t('acr.field.userId.hint')}><TextInput id="acr-user" className="tnum" autoComplete="off" value={change.userId} onChange={(e) => setChange((f) => ({ ...f, userId: e.target.value }))} /></Field>
          <Field label={t('acr.field.newPhone')} htmlFor="acr-new-phone" hint={cIssue('phone')}><TextInput id="acr-new-phone" type="tel" className="tnum" autoComplete="off" value={change.newPhoneE164} onChange={(e) => setChange((f) => ({ ...f, newPhoneE164: e.target.value }))} /></Field>
          <Field label={t('acr.field.reason')} htmlFor="acr-change-reason">
            <Select id="acr-change-reason" value={changeReason} onChange={(e) => setChangeReason(e.target.value as PhoneChangeReasonCode)}>
              {PHONE_CHANGE_REASON_CODES.map((c) => <option key={c} value={c}>{v('account.phone_change_reason', c)}</option>)}
            </Select>
          </Field>
          <CheckRow id="acr-identity" checked={change.identityChecked} onChange={(checked) => setChange((f) => ({ ...f, identityChecked: checked }))}>{t('acr.field.identity')}</CheckRow>
          {cIssue('identity') !== undefined && <Text role="caption" tone="danger" block>{cIssue('identity')}</Text>}
          <Field label={t('acr.field.note')} htmlFor="acr-change-note"><TextArea id="acr-change-note" rows={2} maxLength={500} value={change.note} onChange={(e) => setChange((f) => ({ ...f, note: e.target.value }))} /></Field>
          <Cluster gap={2} wrap><Button type="submit" variant="secondary" disabled={busy} data-testid="acr-change-send">{t('acr.change.send')}</Button></Cluster>
        </Stack>
      </CardForm>

      <Card as="section" aria-label={t('acr.queue.title')} data-testid="acr-queue">
        <Stack gap={3}>
          <strong>{t('acr.queue.title')}</strong>
          {queue !== undefined && queue.length === 0 && <EmptyResult title={t('acr.queue.empty')} />}
          {(queue ?? []).map((r) => (
            <HairlineRow key={r.id} data-testid={`acr-request-${r.id}`}>
              <div>
                <span>{t('acr.queue.row', { id: r.userId, phone: phoneOf(r.newPhoneE164) })}</span>
                <Text role="caption" tone="secondary" block>{v('account.phone_change_reason', r.reasonCode)}{r.note !== undefined ? ` · ${r.note}` : ''}</Text>
                <Text role="caption" tone="secondary" block>{t('acr.queue.expires', { at: r.expiresAt.slice(0, 16).replace('T', ' ') })}</Text>
                {r.mine === true && <Badge tone="neutral">{t('acr.queue.mine')}</Badge>}
                {/* D502 (Q-015): the NEW number is proved with a code before the change can be approved. */}
                {r.state === 'PENDING' && <Badge tone={r.numberVerifiedAt !== undefined ? 'success' : 'warning'}>{t(r.numberVerifiedAt !== undefined ? 'acr.number.confirmed' : 'acr.number.unconfirmed')}</Badge>}
                {r.state === 'PENDING' && r.numberVerifiedAt === undefined && r.mine !== true && <Text role="caption" tone="secondary" block>{t('acr.number.waiting')}</Text>}
                {canVerifyPhoneNumber(r) && (
                  <FilterBar gap={2}>
                    <Field label={t('acr.number.code')} htmlFor={`acr-code-${r.id}`} hint={r.numberAttemptsRemaining !== undefined ? t('acr.number.attempts', { n: r.numberAttemptsRemaining }) : undefined}>
                      <TextInput id={`acr-code-${r.id}`} inputMode="numeric" autoComplete="off" className="tnum" maxLength={6} value={codes[r.id] ?? ''} onChange={(e) => setCodes((c) => ({ ...c, [r.id]: e.target.value }))} />
                    </Field>
                    <Button variant="secondary" size="sm" disabled={!/^[0-9]{6}$/.test((codes[r.id] ?? '').trim())} onClick={() => void onVerify(r)} data-testid={`acr-verify-${r.id}`}>{t('acr.number.verify')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => void onResend(r)} data-testid={`acr-resend-${r.id}`}>{t('acr.number.resend')}</Button>
                  </FilterBar>
                )}
              </div>
              <Cluster gap={2} wrap>
                {canDecidePhoneChange(r, selfApproval) && <Button variant="secondary" size="sm" onClick={() => void onDecide(r, 'approve')} data-testid={`acr-approve-${r.id}`}>{t('acr.queue.approve')}</Button>}
                {canRejectPhoneChange(r) && <Button variant="ghost" size="sm" onClick={() => void onDecide(r, 'reject')}>{t('acr.queue.reject')}</Button>}
                {canWithdrawPhoneChange(r) && <Button variant="ghost" size="sm" onClick={() => void onDecide(r, 'withdraw')}>{t('acr.queue.withdraw')}</Button>}
              </Cluster>
            </HairlineRow>
          ))}
        </Stack>
      </Card>
    </div>
  );
}
